import { Group, Stack } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { FilterBuilder, InfoBox } from "@tfg/ui"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { RDUEComplianceStatus } from "@tfg/types/schemas/enum"
import { filtersRdueSchema } from "@tfg/types/schemas/products"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { InlineBanner } from "~/components/InlineBanner"
import {
  HeaderControlsProvider,
  useHeaderControlsContext,
} from "~/components/contexts/useHeaderControlsContext"
import { usePagination } from "~/components/hooks/usePagination"
import { SearchBar } from "~/components/table/SearchBar"
import { ProductsRDUETAble } from "~/pages/Brand/Regulations/RDUE/ProductsRDUETable.tsx"
import { useProductsRDUEQuery } from "~/pages/Brand/Regulations/RDUE/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

const ProductsRDUEWrapperContent = () => {
  const { cursorBasedPagination, setPageBasedPagination } = usePagination()

  const { filters, setFilters } = useHeaderControlsContext()

  const [debouncedProduct] = useDebouncedValue<string | undefined>(
    filters.search,
    600
  )

  const productsQuery = useProductsRDUEQuery({
    fetchDataOptions: cursorBasedPagination,
    filters: filtersRdueSchema.parse({
      ...filters,
      search: debouncedProduct,
    }),
  })

  useEffect(() => {
    setPageBasedPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [debouncedProduct, setPageBasedPagination])

  const { t } = useTranslation("regulations")
  const { t: tProducts } = useTranslation("products")
  const { user, isAdmin } = useAuth()
  let adminMessage
  if (user?.organization?.settings?.rduePlan === "DISABLED") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("notSubscribed.title")}
          message={t("notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner type="admin" size="xs" message={t("notSubscribed.title")}>
          {adminMessage}
        </InlineBanner>
      )}
      <DataFetchWrapper query={productsQuery}>
        {({ data, isFetching }) => (
          <Stack>
            <Group>
              <SearchBar
                filtersValue={filters}
                setFiltersValue={setFilters}
                placeholder={tProducts("lists.placeholders.searchProduct")}
                isFetching={isFetching}
              />
              <FilterBuilder
                setFilters={setFilters}
                filters={filters}
                options={[
                  {
                    label: t("lists.placeholders.rdueStatus"),
                    key: "rdueCompliance",
                    type: "select",
                    options: Object.entries(RDUEComplianceStatus).map(
                      ([key, value]) => ({
                        value: key,
                        label: value,
                      })
                    ),
                  },
                ]}
              />
            </Group>

            <ProductsRDUETAble
              data={data}
              fetchDataOptions={cursorBasedPagination}
              setPagination={setPageBasedPagination}
            />
          </Stack>
        )}
      </DataFetchWrapper>
    </Stack>
  )
}

export const ProductsRDUEWrapper = () => {
  return (
    <HeaderControlsProvider>
      <ProductsRDUEWrapperContent />
    </HeaderControlsProvider>
  )
}
